import { useSeoMeta } from '@unhead/react';
import { useState } from 'react';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Languages } from 'lucide-react';
import { FULL_ORDER_OF_MASS, ICEL_COPYRIGHT } from '@/lib/liturgical/ordoMissae';
import type { MassPart } from '@/lib/liturgical/ordoMissae';

export default function OrdoPage() {
  useSeoMeta({
    title: 'Order of Mass — Nostr Missal',
    description: 'The complete Order of Mass (Ordo Missae) — Third Typical Edition, in English and Latin.',
  });

  const [showLatin, setShowLatin] = useState(false);

  return (
    <Layout>
      <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
        {/* Title */}
        <div className="mb-10 text-center">
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-stone-400 dark:text-stone-500">
            Third Typical Edition
          </p>
          <h1 className="text-3xl font-semibold text-stone-900 dark:text-stone-100 sm:text-4xl" style={{ fontFamily: '"Cinzel", serif' }}>
            Order of Mass
          </h1>
          <p className="mt-2 text-base italic text-stone-400 dark:text-stone-500">Ordo Missae</p>
          <p className="text-stone-300 dark:text-stone-600 text-lg tracking-[0.5em] mt-4 mb-4 select-none">✦ ✦ ✦</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowLatin(!showLatin)}
            className="gap-1.5 text-stone-600 dark:text-stone-300 border-stone-200 dark:border-stone-700"
          >
            <Languages className="h-3.5 w-3.5" />
            {showLatin ? 'Hide Latin' : 'Show Latin'}
          </Button>
        </div>

        {/* Sections */}
        <div className="space-y-12">
          {FULL_ORDER_OF_MASS.map((group) => (
            <section key={group.group}>
              {/* Group heading — plain text divider, no pill/fill */}
              <div className="mb-6 flex items-center gap-4">
                <div className="flex-1 border-t border-stone-200 dark:border-stone-700" />
                <h2
                  className="text-[11px] font-bold uppercase tracking-[0.2em] text-stone-500 dark:text-stone-400 shrink-0"
                  style={{ fontFamily: '"Cinzel", serif' }}
                >
                  {group.group}
                </h2>
                <div className="flex-1 border-t border-stone-200 dark:border-stone-700" />
              </div>

              <div className="space-y-3">
                {group.sections.map((section) => (
                  <div key={section.id} className="rounded-xl border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 px-5 pt-5 pb-6">
                    <h3 className="mb-1 text-[13px] font-bold tracking-[0.12em] uppercase text-stone-700 dark:text-stone-300" style={{ fontFamily: '"Cinzel", serif' }}>
                      {section.title}
                    </h3>
                    {section.rubric && (
                      <p className="mb-3 text-[13px] italic text-red-600 dark:text-red-400">{section.rubric}</p>
                    )}
                    <div className="mt-3 space-y-3 text-[17px]">
                      {section.parts.map((part, i) => (
                        <PartLine key={i} part={part} showLatin={showLatin} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>

        {/* Copyright */}
        <div className="mt-12 border-t border-stone-200 dark:border-stone-700 pt-6">
          <p className="text-[11px] leading-relaxed text-stone-400 dark:text-stone-500">{ICEL_COPYRIGHT}</p>
        </div>
      </div>
    </Layout>
  );
}

// Speaker labels — plain uppercase, no color coding
const SPEAKER_LABEL: Record<string, string> = {
  priest: 'Priest',
  people: 'People',
  deacon: 'Deacon',
  all: 'All',
  lector: 'Lector',
};

function PartLine({ part, showLatin }: { part: MassPart; showLatin: boolean }) {
  // Rubrics stay red — this is the traditional typographic convention in every printed missal
  if (part.speaker === 'rubric') {
    return <p className="text-[13px] italic text-red-600 dark:text-red-400 leading-relaxed">{part.text}</p>;
  }

  const label = part.speaker ? SPEAKER_LABEL[part.speaker] : undefined;

  return (
    <div className={part.isResponse ? 'ml-4' : ''}>
      {label && (
        <span className="inline-block mb-0.5 text-[10px] font-bold uppercase tracking-[0.15em] text-stone-400 dark:text-stone-500">
          {label}:
        </span>
      )}
      <p className={`whitespace-pre-wrap leading-[1.85] text-stone-800 dark:text-stone-200 ${part.isResponse ? 'font-semibold' : ''}`}>
        {part.text}
      </p>
      {showLatin && part.latin && (
        <p className="mt-1.5 whitespace-pre-wrap leading-[1.85] text-[0.88em] italic text-stone-400 dark:text-stone-500 pl-3 border-l border-stone-200 dark:border-stone-700">
          {part.latin}
        </p>
      )}
    </div>
  );
}
